import React from 'react'
import moment from 'moment'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import Style from './IncidentFeed.module.css'
import Loading from '../common/LoaderWrapper/loading'
import BasicECharts from '../charts/BasicEChart'
import { LOAD_ERROR } from './messages'

echarts.use([TooltipComponent, GridComponent, LineChart, CanvasRenderer])

// ===========================================================================
// One Daily activity panel (PIXMC-6521).
//
// Built on charts/BasicEChart.js, whose default option is already a sparkline base
// (hidden axes, zero grid) — only the series type and the tooltip differ here.
//
// Both series this renders arrive REVERSE-CHRONOLOGICAL (newest first), as every
// existing chart endpoint returns them. A trend reads left-to-right oldest-to-newest, so
// the arrays are reversed here rather than with echarts' `inverse: true` — the tooltip
// then reports the date the user is actually hovering.
//
// Reads { detectionTime, records }. The phishing series is { dtcTime, rcds } on the wire
// and is normalised in the incidentFeed slice, so one panel serves both.
// ===========================================================================

const ACCENT = '#3162a7'

const TrendPanel = ({ name, subtitle, records, loading, error, emptyText, formatValue }) => {
  const series = Array.isArray(records) ? [...records].reverse() : []
  const labels = series.map((item) => moment(new Date(item.detectionTime)).format('D MMM'))
  const values = series.map((item) => item.records)
  const format = formatValue || ((value) => Number(value || 0).toLocaleString())

  const options = {
    color: [ACCENT],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: (params) => {
        const point = Array.isArray(params) ? params[0] : params
        return `${point.axisValue} — <strong>${format(point.data)}</strong>`
      },
    },
    xAxis: { type: 'category', data: labels, show: false, boundaryGap: false },
    series: [
      {
        type: 'line',
        data: values,
        showSymbol: false,
        symbol: 'circle',
        smooth: false,
        lineStyle: { width: 2, color: ACCENT },
        // The mockup's filled area under the line.
        areaStyle: { color: ACCENT, opacity: 0.12 },
      },
    ],
  }

  const renderChart = () => {
    if (loading) return <Loading loading={true} />
    // A failed call must not render as "no data" — that reads as a clean window and is
    // the same conflation D-7 forbids for the ring. Never the API's own message.
    if (error) return <div className={Style.trendEmpty}>{LOAD_ERROR}</div>
    if (series.length === 0) {
      return <div className={Style.trendEmpty}>{emptyText || 'No data for this window'}</div>
    }
    return (
      <BasicECharts
        echarts={echarts}
        options={options}
        style={{ width: '100%', height: '90px' }}
      />
    )
  }

  return (
    <div className={Style.trendCell}>
      <div className={Style.trendName}>{name}</div>
      <div className={Style.trendTotal}>{subtitle}</div>
      <div className={Style.spark}>{renderChart()}</div>
      {series.length > 0 && !loading && !error && (
        <div className={Style.axisRow}>
          <span>{labels[0]}</span>
          <span>{labels[labels.length - 1]}</span>
        </div>
      )}
    </div>
  )
}

export default TrendPanel
